import { AiMessageRoleEnum, AiMessageStatusEnum } from 'graphql-common';
import { isAiStreamTerminalStatus } from '@lib/utils/ai/streamReducer';
import isAiToolActivityMessage from './isAiToolActivityMessage';

type RenderableCandidate = {
  role: AiMessageRoleEnum;
  content?: string | null;
  status?: AiMessageStatusEnum | null;
  toolCalls?: { id: string }[] | null;
};

// Whether a transcript row has anything to draw.
//
// Assistant rows are kept when they carry answer text, tool calls (rendered as
// an activity row), or — since the BE now keeps the blank assistant rows of
// terminally failed turns so the outcome survives a reload (guide §5) — error
// copy for their status/errorCode. In-flight rows are excluded: the live turn is
// drawn from the stream, so rendering them too would duplicate it.
//
// Exactly one shape gets dropped here: a turn the user stopped before any text
// streamed (CANCELLED / stopped_by_user). Its copy is deliberately silent —
// guide §6 says the partial text IS the result, and this row has none — so with
// no content and no banner it would draw a bubble containing nothing but a
// timestamp.
//
// `errorText` is passed in rather than derived so this stays pure and leaves
// getAiErrorText the single owner of the status/errorCode → copy mapping.
export default function isRenderableAiMessage(
  message: RenderableCandidate,
  errorText: string | null,
): boolean {
  if (message.role === AiMessageRoleEnum.User) return true;
  if (message.role !== AiMessageRoleEnum.Assistant) return false;
  if (isAiToolActivityMessage(message)) return true;
  if (!isAiStreamTerminalStatus(message.status)) return false;
  return !!message.content || !!errorText;
}
